import type { EmailAgentConfig } from "@/lib/agents/email/types"

export type ScheduledEmailBlock = "dry_run" | "draft_review"

export const SCHEDULED_EMAIL_AGENT_REQUIREMENT =
  "Only active Email Agents with a connected sending mailbox and category delivery set to Send are available. Change any Dry Run or Create Drafts modes to Send in General → Category Settings."

/** Scheduled sends need a target agent that can send in every enabled category. */
export function getScheduledEmailAssignmentBlock(config: EmailAgentConfig | null | undefined): ScheduledEmailBlock | null {
  const settings = config?.auto_reply
  const enabledCategories = Object.values(settings?.category_settings ?? {})
    .filter(category => category.enabled)
  if (enabledCategories.length === 0) return getBlock(settings)

  for (const category of enabledCategories) {
    const block = getBlock(category, settings)
    if (block) return block
  }
  return null
}

/** A classified thread uses its enabled category; other tasks use the safe all-category check. */
export function getScheduledEmailDeliveryBlock(
  config: EmailAgentConfig | null | undefined,
  category?: string,
): ScheduledEmailBlock | null {
  const settings = config?.auto_reply
  const categorySettings = category ? settings?.category_settings?.[category] : undefined
  if (categorySettings?.enabled) return getBlock(categorySettings, settings)
  return getScheduledEmailAssignmentBlock(config)
}

function getBlock(
  settings: { dry_run?: boolean; create_draft_instead_of_send?: boolean } | null | undefined,
  fallback?: { dry_run?: boolean; create_draft_instead_of_send?: boolean } | null,
): ScheduledEmailBlock | null {
  if (settings?.dry_run ?? fallback?.dry_run) return "dry_run"
  if (settings?.create_draft_instead_of_send ?? fallback?.create_draft_instead_of_send) return "draft_review"
  return null
}

export function scheduledEmailBlockMessage(block: ScheduledEmailBlock): string {
  switch (block) {
    case "dry_run": return "This Email Agent uses Dry Run. Set its category delivery modes to Send in General before assigning scheduled email tasks."
    case "draft_review": return "This Email Agent requires draft review. Set its category delivery modes to Send in General before assigning scheduled email tasks."
    default: {
      const exhaustive: never = block
      return exhaustive
    }
  }
}
